import { Button } from '@ctw/shared/components/button';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faTrash, faTrashUndo } from '@fortawesome/pro-duotone-svg-icons';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
// import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type PropsWithChildren, type ReactNode, createContext, useContext, useState } from 'react';
import type { MergeExclusive } from 'type-fest';

export interface PatientFactGroup {
  label: string;
  facts: Array<PatientFact>;
}

interface PatientFactNotFound {
  id: string;
  label: string;
}

interface PatientFactFound {
  id: string;
  label: string;
  value: ReactNode;
  initiallyExpanded?: boolean;
  relatedBinaryId?: string;
}

export type PatientFact = MergeExclusive<PatientFactFound, PatientFactNotFound>;

const isFoundFact = (fact: PatientFact): fact is PatientFactFound => Object.hasOwn(fact, 'value');
const isNotFoundFact = (fact: PatientFact): fact is PatientFactNotFound =>
  !Object.hasOwn(fact, 'value');

export interface PatientFactCallbackArgs {
  fact: PatientFact;
}

interface PatientFactExtractionGroupedTableState {
  isRedacted?: (fact: PatientFact) => boolean;
  onRedactFact?: (args: PatientFactCallbackArgs) => void;
  onExpandFact?: (args: PatientFactCallbackArgs) => void;
  onCollapseFact?: (args: PatientFactCallbackArgs) => void;
}

const PatientFactExtractionGroupedTableContext = createContext<
  PatientFactExtractionGroupedTableState | undefined
>(undefined);

interface PatientFactExtractionGroupedTableProviderProps
  extends PropsWithChildren,
    PatientFactExtractionGroupedTableState {}

const PatientFactExtractionGroupedTableProvider = ({
  children,
  ...tableState
}: PatientFactExtractionGroupedTableProviderProps) => (
  <PatientFactExtractionGroupedTableContext.Provider value={tableState}>
    {children}
  </PatientFactExtractionGroupedTableContext.Provider>
);

const usePatientFactExtractionGroupedTableState = () => {
  const context = useContext(PatientFactExtractionGroupedTableContext);

  if (!context) {
    throw new Error(
      'usePatientFactExtractionGroupedTableState must be used within a PatientFactExtractionGroupedTable',
    );
  }

  return context;
};

export interface PatientFactExtractionGroupedTableProps
  extends PatientFactExtractionGroupedTableState {
  factGroups: Array<PatientFactGroup>;
}

export const PatientFactExtractionGroupedTable = ({
  factGroups,
  ...tableState
}: PatientFactExtractionGroupedTableProps) => (
  <PatientFactExtractionGroupedTableProvider {...tableState}>
    <div
      className={tw`divide-y divide-divider-main overflow-hidden rounded-md border border-border-main/80`}
    >
      {factGroups.map((factGroup) => (
        <PatientFactExtractionTableGroup key={factGroup.label} factGroup={factGroup} />
      ))}
    </div>
  </PatientFactExtractionGroupedTableProvider>
);

interface PatientFactExtractionTableGroupProps {
  factGroup: PatientFactGroup;
}

const PatientFactExtractionTableGroup = ({ factGroup }: PatientFactExtractionTableGroupProps) => (
  <div>
    <div
      className={tw`border-divider-main border-b bg-content-subtle/10 px-4 py-2 font-bold text-content-main/65 text-xs uppercase tracking-wide`}
    >
      {factGroup.label}
    </div>
    <div className={tw`divide-y divide-divider-main`}>
      {factGroup.facts.map((fact) => (
        <PatientFactExtractionTableItem key={fact.label} fact={fact} />
      ))}
    </div>
  </div>
);

interface PatientFactExtractionTableItemProps {
  fact: PatientFact;
}

const PatientFactExtractionTableItem = ({ fact }: PatientFactExtractionTableItemProps) => {
  const { isRedacted, onRedactFact, onExpandFact, onCollapseFact } =
    usePatientFactExtractionGroupedTableState();
  const [isExpanded, setIsExpanded] = useState(fact.initiallyExpanded ?? false);
  const redacted = isRedacted?.(fact) ?? false;

  const handleToggleCitation = () => {
    const nextExpanded = !isExpanded;
    setIsExpanded(nextExpanded);
    if (nextExpanded) {
      onExpandFact?.({ fact });
    } else {
      onCollapseFact?.({ fact });
    }
  };

  const handleToggleRedaction = () => {
    onRedactFact?.({ fact });
  };

  return (
    <div
      className={twx(
        'flex min-h-[3rem] items-center justify-between gap-4 px-4 py-2.5 hover:bg-content-disabled/5',
        {
          'bg-caution-background/70': !redacted && isNotFoundFact(fact),
          'bg-error-background/30': redacted,
        },
      )}
    >
      <div className={tw`flex items-center gap-4`}>
        <div
          className={tw`flex shrink-0 basis-48 items-center gap-0.5 overflow-hidden font-medium text-content-subtle text-sm`}
        >
          <div
            className={twx('truncate font-medium text-content-subtle text-sm', {
              'line-through': redacted,
            })}
          >
            {fact.label}
          </div>
        </div>
        <div className={tw`min-w-0 flex-1 truncate font-medium text-sm`}>
          {isFoundFact(fact) ? (
            <span
              className={twx({
                'text-content-main': !redacted,
                'text-content-subtle line-through': redacted,
              })}
            >
              {fact.value}
            </span>
          ) : (
            <span
              className={twx({
                'text-caution-main italic': !redacted,
                'text-content-subtle line-through': redacted,
              })}
            >
              Not found
            </span>
          )}
        </div>
      </div>
      <div className={tw`flex items-center`}>
        {fact.relatedBinaryId && (
          <Button
            className={twx(
              'group/citation-button active:!scale-[.99] flex aspect-square items-center justify-center rounded-md p-1 transition-all duration-150',
              {
                'bg-info-main/10 shadow-sm': isExpanded,
                'drop-shadow-sm': !isExpanded,
                'opacity-40': redacted,
                'hover:bg-info-main/10': !redacted,
              },
            )}
            type="button"
            variant="unstyled"
            onClick={handleToggleCitation}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Hide citation' : 'Show citation'}
            disabled={redacted}
          >
            <FontAwesomeIcon
              icon={faFileLines}
              className={twx('h-3.5 w-3.5', {
                'text-info-text': isExpanded,
                'text-content-icon': !isExpanded,
                'group-hover/citation-button:text-info-text': !redacted,
              })}
            />
          </Button>
        )}
        <Button
          className={twx(
            'group/delete-button active:!scale-[.90] flex aspect-square items-center justify-center rounded-md p-1 drop-shadow-sm transition-all duration-150 hover:shadow-sm',
            {
              'hover:bg-error-main/10': !redacted,
              'hover:bg-success-main/10': redacted,
            },
          )}
          type="button"
          variant="unstyled"
          onClick={handleToggleRedaction}
          aria-label={redacted ? 'Restore fact' : 'Remove fact'}
        >
          <FontAwesomeIcon
            icon={redacted ? faTrashUndo : faTrash}
            className={twx('h-3.5 w-3.5 text-content-icon', {
              'group-hover/delete-button:text-error-text': !redacted,
              'group-hover/delete-button:text-success-text': redacted,
            })}
          />
        </Button>
      </div>
    </div>
  );
};
